/**
 * warehouseRecurringEvents.js
 * Plans how recurring events added in the Settings warehouse panel get saved to
 * the warehouse and all of its docks.
 *
 * Slot availability reads only a dock's own calendar. It falls back to the
 * warehouse calendar only when the dock has none. So an event saved only on the
 * warehouse calendar never blocks booking at a dock that has its own calendar.
 * Each new event is therefore written to the warehouse calendar and to every
 * distinct dock calendar, in one request.
 */
import {
  buildRecurringEventPayload,
  findRecurringEventOverlap,
  firstYearlyOccurrenceOnOrAfter,
  hasIncompleteEndDate,
  isUnsavedRecurringEvent,
} from "./recurringEventUtils";

const quoteName = (entry) => `"${entry?.eventName || "Recurring Event"}"`;

const sameRef = (a, b) => String(a ?? "").toLowerCase() === String(b ?? "").toLowerCase();

// The overlap scan needs an effective date on both sides. An event without one
// is never active, so it can't conflict with anything.
const canOverlap = (a, b) => Boolean(a?.effectiveDate && b?.effectiveDate);

/**
 * @returns {{ targets: Array<{calendarId, label}>, payload: object[], problems: string[] }}
 *   `payload` is the body for one POST /api/events/events. It is only usable
 *   when `problems` is empty.
 */
export const planWarehouseRecurringRollout = ({
  warehouseName,
  warehouseSettings,
  docks = [],
  docksLoadFailed = false,
  drafts = [],
}) => {
  const problems = [];
  const warehouseCalendarId = warehouseSettings?.calendarId;

  if (warehouseCalendarId === undefined || warehouseCalendarId === null) {
    return {
      targets: [],
      payload: [],
      problems: ["The warehouse calendar could not be loaded. Refresh the page and try again."],
    };
  }

  // Without the dock list, the warehouse would look dockless and the events would
  // never reach the docks' own calendars.
  if (docksLoadFailed) {
    return {
      targets: [],
      payload: [],
      problems: ["The warehouse's docks could not be loaded. Refresh the page and try again."],
    };
  }

  drafts.forEach((draft) => {
    const name = quoteName(draft);
    if (!draft.effectiveDate) {
      problems.push(`Pick an effective date for ${name}.`);
    }
    if (hasIncompleteEndDate(draft)) {
      problems.push(`Pick an end date for ${name}, or choose Indefinitely.`);
    }
    if (!draft.allDay) {
      if (!draft.startTime || !draft.endTime) {
        problems.push(`Pick a start and end time for ${name}.`);
      } else if (draft.startTime === draft.endTime) {
        // The backend reads equal start/end as a whole-day closure.
        problems.push(`The start and end time for ${name} are the same. Use All Day to close the whole day.`);
      }
    }
    if (draft.recurrencePattern === "WEEKLY" && !(draft.daysOfWeek || []).length) {
      problems.push(`Pick at least one day for ${name}.`);
    }
    if (draft.recurrencePattern === "MONTHLY" && draft.monthlyMode !== "DAY_OF_WEEK" && !draft.dayOfMonth) {
      problems.push(`Pick a day of the month for ${name}.`);
    }
    if (draft.recurrencePattern === "YEARLY") {
      if (!draft.monthOfYear || !draft.dayOfMonth) {
        problems.push(`Pick a month and day for ${name}.`);
      } else if (
        draft.effectiveDate &&
        !firstYearlyOccurrenceOnOrAfter(draft.effectiveDate, draft.monthOfYear, draft.dayOfMonth)
      ) {
        problems.push(`Month ${draft.monthOfYear}, day ${draft.dayOfMonth} for ${name} isn't a real date.`);
      }
    }
  });

  const savedOnly = (settings) => (settings?.recurringEvents || []).filter((re) => !isUnsavedRecurringEvent(re));

  const targets = [
    {
      calendarId: warehouseCalendarId,
      label: warehouseName || "the warehouse",
      existing: savedOnly(warehouseSettings),
    },
  ];
  const seenCalendarIds = new Set([String(warehouseCalendarId)]);

  docks.forEach((dock, idx) => {
    const label = dock?.dockName || `Dock ${idx + 1}`;
    const dockSettings = dock?.settings || {};

    // CalendarSettings falls back to the warehouse's settings when a dock's own
    // calendar fails to load. Writing to that fallback would skip the dock.
    if (dock?.calendarRefId && !sameRef(dockSettings.calendarRefId, dock.calendarRefId)) {
      problems.push(`The calendar for ${label} could not be loaded. Refresh the page and try again.`);
      return;
    }

    // A dock with no calendar of its own books against the warehouse calendar,
    // which is already a target.
    const calendarId = dockSettings.calendarId;
    if (calendarId === undefined || calendarId === null || seenCalendarIds.has(String(calendarId))) return;
    seenCalendarIds.add(String(calendarId));

    targets.push({ calendarId, label, existing: savedOnly(dockSettings) });
  });

  // Same rule the Edit Dock modal enforces. A conflict pushed onto a dock would
  // block that dock's next save until someone untangled it.
  targets.forEach((target) => {
    drafts.forEach((draft) => {
      target.existing.forEach((existing) => {
        if (!canOverlap(draft, existing)) return;
        const { overlaps, date } = findRecurringEventOverlap(draft, existing);
        if (overlaps) {
          problems.push(
            `${quoteName(draft)} overlaps ${quoteName(existing)} on ${target.label} (${date}).`
          );
        }
      });
    });
  });

  for (let i = 0; i < drafts.length; i++) {
    for (let j = i + 1; j < drafts.length; j++) {
      if (!canOverlap(drafts[i], drafts[j])) continue;
      const { overlaps, date } = findRecurringEventOverlap(drafts[i], drafts[j]);
      if (overlaps) {
        problems.push(`${quoteName(drafts[i])} overlaps ${quoteName(drafts[j])} (${date}).`);
      }
    }
  }

  const payload = targets.flatMap((target) =>
    drafts.map((draft) => buildRecurringEventPayload(draft, target.calendarId))
  );

  return {
    targets: targets.map(({ calendarId, label }) => ({ calendarId, label })),
    payload,
    problems,
  };
};
